import { ApiResponse, DataObject, DataPaginate, Role } from "@/types";
import { AspirationResponse, CategoryAspirationResponse } from "./interface";
import { fetcher, sendData } from "@/services/api/fetcher";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useFormMutation } from "@/hooks/useFormMutation";
import { AspirationPayload, CategoryAspirationPayload } from "./validation";
import useGetToken from "@/hooks/useGetToken";

const getAspiration = async (
  query?: string,
  role?: Role | "guest",
  isMemberAdmin?: boolean
): Promise<ApiResponse<DataPaginate<AspirationResponse>>> => {
  let api = `aspiration?${query}`;
  switch (role) {
    case "anggota":
      if (isMemberAdmin) {
        api = `aspiration/admin/all?${query}`;
      } else {
        api = `aspiration/anggota/all?${query}`;
      }
      break;
    case "user":
      api = `aspiration/user/all?${query}`;
      break;
    default:
      api = `aspiration?${query}`;
      break;
  }
  return await fetcher(api, 0);
};

export const useAspiration = (query?: string, userRole?: Role | "guest") => {
  const { decode } = useGetToken();
  const role = userRole ? userRole : decode?.role;

  const isFraksiAdmin =
    role === "anggota" && decode?.fraksi && !decode?.dapil && !decode?.komisi;
  const isDapilAdmin =
    role === "anggota" && decode?.dapil && !decode?.fraksi && !decode?.komisi;
  const isKomisiAdmin =
    role === "anggota" && decode?.komisi && !decode?.fraksi && !decode?.dapil;

  const isMemberAdmin =
    isFraksiAdmin || isDapilAdmin || isKomisiAdmin ? true : false;
  return useQuery<ApiResponse<DataPaginate<AspirationResponse>>, Error>({
    queryKey: ["useAspiration", query, role, isMemberAdmin],
    queryFn: () => getAspiration(query, role, isMemberAdmin),
  });
};

export const getAspirationDetail = async (
  id: number
): Promise<ApiResponse<DataObject<AspirationResponse>>> => {
  return await fetcher(`aspiration/${id}`);
};

export const useAspirationDetail = (id: number) => {
  return useQuery<ApiResponse<DataObject<AspirationResponse>>, Error>({
    queryKey: ["useAspirationDetail", id],
    queryFn: () => getAspirationDetail(id),
  });
};

// kategori aspirasi
const getCategoryAspiration = async (
  query?: string
): Promise<ApiResponse<DataPaginate<CategoryAspirationResponse>>> => {
  return await fetcher(`master/category-aspiration?${query}`);
};
export const useGetCategoryAspiration = (query?: string) => {
  return useQuery<ApiResponse<DataPaginate<CategoryAspirationResponse>>, Error>(
    {
      queryKey: ["useGetCategoryAspiration", query],
      queryFn: () => getCategoryAspiration(query),
    }
  );
};
const getCategoryAspirationDetail = async (
  id: number
): Promise<ApiResponse<DataObject<CategoryAspirationResponse>>> => {
  return await fetcher(`master/category-aspiration/${id}`);
};
export const useGetCategoryAspirationDetail = (id: number) => {
  return useQuery<ApiResponse<DataObject<CategoryAspirationResponse>>, Error>({
    queryKey: ["useGetCategoryAspirationDetail", id],
    queryFn: () => getCategoryAspirationDetail(id),
  });
};

// mutation
export const useAspirationMutation = (
  method: "POST" | "PUT" = "POST",
  id?: number
) => {
  return useFormMutation<
    ApiResponse<DataObject<AspirationResponse>>,
    Error,
    AspirationPayload
  >({
    mutationFn: async (
      data
    ): Promise<ApiResponse<DataObject<AspirationResponse>>> => {
      const endpoint = id ? `aspiration/${id}` : "aspiration";
      const delay = new Promise((resolve) => setTimeout(resolve, 2000));
      const response: ApiResponse<DataObject<AspirationResponse>> =
        await sendData(endpoint, data, method, true);
      await delay;
      return response;
    },
    loadingMessage:
      method === "POST" ? "Mengirim usulan..." : "Memperbarui data...",
    successMessage:
      method === "POST"
        ? "Usulan berhasil ditambahkan"
        : "Data berhasil diperbarui",
  });
};

export const useAspirationVerificationMutation = (id?: number) => {
  return useFormMutation<
    ApiResponse<DataObject<AspirationResponse>>,
    Error,
    AspirationPayload
  >({
    mutationFn: async (
      data
    ): Promise<ApiResponse<DataObject<AspirationResponse>>> => {
      const endpoint = `aspiration/verification-status/${id}`;
      const delay = new Promise((resolve) => setTimeout(resolve, 2000));
      const response: ApiResponse<DataObject<AspirationResponse>> =
        await sendData(endpoint, data, "PUT");
      await delay;
      return response;
    },
    loadingMessage: "Verifikasi data...",
    successMessage: "Data berhasil di verifikasi",
  });
};

export const useCategoryAspirationMutation = (
  method: "POST" | "PUT" = "POST",
  id?: number
) => {
  return useFormMutation<
    ApiResponse<DataObject<CategoryAspirationResponse>>,
    Error,
    CategoryAspirationPayload
  >({
    mutationFn: async (
      data
    ): Promise<ApiResponse<DataObject<CategoryAspirationResponse>>> => {
      const endpoint = id
        ? `master/category-aspiration/${id}`
        : "master/category-aspiration";
      const delay = new Promise((resolve) => setTimeout(resolve, 2000));
      const response: ApiResponse<DataObject<CategoryAspirationResponse>> =
        await sendData(endpoint, data, method);
      await delay;
      return response;
    },
    loadingMessage:
      method === "POST" ? "Menyimpan data..." : "Memperbarui data...",
    successMessage:
      method === "POST"
        ? "Data berhasil ditambahkan"
        : "Data berhasil diperbarui",
  });
};

export const usePatchAspirationStatus = (id: string) => {
  return useMutation<ApiResponse<DataObject<AspirationResponse>>, Error, any>({
    mutationFn: async (): Promise<
      ApiResponse<DataObject<AspirationResponse>>
    > => {
      const endpoint = `aspiration/publish/${id}`;
      const response: ApiResponse<DataObject<AspirationResponse>> =
        await sendData(endpoint, {}, "PATCH");
      return response;
    },
  });
};
